import { cn } from '../../lib/cn'
import { Brandlock } from './Brandlock'
import { Container } from './Container'
import { PRODUCT_LONG_NAME } from '../../lib/product'

interface JoinPageProps {
  children: React.ReactNode
  /**
   * Roman numeral for the step, e.g. "I". Omitted by pages that use this
   * chrome without being a step of the join flow — /sign-in is a door, not
   * step n of anything, and inventing a numeral would be a lie in the chrome.
   */
  stepNumeral?: string
  /** Step name, e.g. "The Application". */
  stepLabel: string
}

/**
 * Chrome for the join flow: a slim crest bar carrying the step, and a quiet
 * foot. Deliberately narrower than ClubPage — once someone is applying, the
 * marketing nav is just a set of ways to abandon the form.
 */
export function JoinPage({ children, stepNumeral, stepLabel }: JoinPageProps) {
  return (
    <div className="flex min-h-screen flex-col">
      <div className="sticky top-0 z-20 border-b border-brass/35 bg-paper/90 backdrop-blur-md">
        <Container className="flex min-h-[66px] flex-wrap items-center justify-between gap-3 py-3 sm:py-0">
          <Brandlock />
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-soft">
            {stepNumeral && (
              <b className="mr-2.5 font-display font-semibold tracking-[0.1em] text-brass-deep">
                {stepNumeral} ·
              </b>
            )}
            {stepLabel}
          </p>
        </Container>
      </div>

      {/* The stage clips sideways spill (`overflow-x-clip`, never `hidden`,
          which would also cut the band's 32px reach into the foot): a screen's
          own decoration — the welcome page's 410px halo ring at 390px — must
          not pan the page either, the way the club's sections wrap their halo
          rings in `overflow-hidden`. `clip` makes no scroll container, so
          nothing inside changes how it scrolls or sticks. */}
      <main
        id="main"
        tabIndex={-1}
        className="relative flex-1 overflow-x-clip px-6 pb-14 pt-18 outline-none sm:px-10"
      >
        {/* Fairway contours along the foot of the stage. `w-full` is
            load-bearing: an SVG with only a viewBox is a replaced element, so
            `inset-x-0` alone sizes it from its aspect ratio (320px tall ×
            1200/320 = 1200px wide) and drops `right:0` as over-constrained —
            the band then ran 1200px wide at every viewport and panned a phone
            810px sideways. An explicit width lets `preserveAspectRatio="none"`
            map the drawing onto the stage's own width, as the landing hero and
            `PageHead` do. */}
        <svg
          viewBox="0 0 1200 320"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -bottom-8 z-0 h-80 w-full opacity-40"
        >
          <path d="M-50 120 Q300 60 620 130 T1250 100" stroke="var(--color-sage)" fill="none" strokeWidth="1.5" />
          <path d="M-50 160 Q300 105 620 170 T1250 145" stroke="var(--color-brass)" fill="none" strokeWidth="1" opacity=".5" />
          <path d="M-50 210 Q320 160 640 225 T1250 205" stroke="var(--color-fairway)" fill="none" strokeWidth="1.5" opacity=".7" />
        </svg>
        <div className="relative z-10">{children}</div>
      </main>

      <footer className="relative z-10 px-6 pb-7 pt-8 text-center text-xs text-ink-soft">
        © MMXXVI {PRODUCT_LONG_NAME}
      </footer>
    </div>
  )
}

/**
 * The raised stationery card the join flow is written on: brass frame with an
 * inset hairline, the club's formal register.
 */
export function JoinCard({
  children,
  className,
  as: Tag = 'div',
}: {
  children: React.ReactNode
  className?: string
  /** Element to render as — `aside` for the checkout's order summary. */
  as?: 'div' | 'aside' | 'section'
}) {
  return (
    <Tag
      className={cn(
        'relative mx-auto flex w-full max-w-[440px] flex-col rounded-club-lg border border-brass/55 bg-parchment',
        'px-8 pb-9 pt-12 shadow-[0_30px_60px_-38px_rgba(21,58,42,0.45)] sm:px-11',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-2 rounded-club border border-brass/28"
      />
      <div className="relative z-10 flex flex-1 flex-col">{children}</div>
    </Tag>
  )
}
